import { useCallback, useMemo } from "react";
import { ImageViewerModal } from "@/components/common/image-viewer-modal/image-viewer-modal";
import { useCollectionItems } from "@/components/reports/collection-detail-view/use-collection-items";
import { useCanOnResource, usePermissions } from "@/hooks/use-permissions";
import { useModalNavigation } from "@/hooks/use-modal-navigation";
import { coverImage } from "@/lib/record-images";
import type { ProjectDetail, ProjectRecord } from "@/types/api";

interface ReportRecordViewerProps {
  projectId: string;
  collectionId: string;
  project: ProjectDetail;
  /** The open record, or null when none is ([[report-detail-page.spec]] `?record=`). */
  record: ProjectRecord | null;
  /** The frame the report page gives it. */
  presentation: "dialog" | "fullscreen" | "panel";
  onClose: () => void;
  /** Previous/next moved to another of the report's photos. */
  onNavigate: (record: ProjectRecord) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

/** Stable extractors so useModalNavigation's memo and effects don't re-run. */
const getRecordId = (r: ProjectRecord) => r.id;
const getRecordImageUrl = (r: ProjectRecord) => coverImage(r)?.image ?? null;

/**
 * A photo opened from a photos report: the Photos page's record view, wired the
 * way [[records-view.spec]] wires it, but stepping through the report's photos
 * rather than the project's. See [[report-record-viewer.spec]].
 */
export function ReportRecordViewer({
  projectId,
  collectionId,
  project,
  record,
  presentation,
  onClose,
  onNavigate,
  onUnsavedChangesChange,
}: ReportRecordViewerProps) {
  const permissionsProject = {
    owner: project.owner.id,
    my_roles: project.my_roles,
  };
  const { can } = usePermissions(permissionsProject);
  const canOnResource = useCanOnResource(permissionsProject);

  // The same query the report's grid renders (deduped by key), so stepping
  // follows the grid's order and shares its pages. Tombstones have nothing to
  // open and are skipped.
  const {
    data: itemData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useCollectionItems(projectId, collectionId);
  const records = useMemo(
    () =>
      (itemData?.items ?? []).flatMap((item) =>
        item.record && !item.is_deleted ? [item.record] : [],
      ),
    [itemData],
  );
  // Joins a page already in flight (the grid's own scroll loader may have asked
  // for it) rather than cancelling and re-asking.
  const loadNextPage = useCallback(() => {
    void fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage]);

  const {
    onPrev,
    onNext,
    currentIndex,
    totalCount,
    isFetchingNextPage: navIsFetchingNextPage,
  } = useModalNavigation({
    items: records,
    selectedId: record?.id ?? null,
    getId: getRecordId,
    getImageUrl: getRecordImageUrl,
    onNavigate,
    // A failed page stops stepping at the last loaded photo, as it stops the
    // report's scroll: nothing re-asks on its own, and the report's Try again
    // resumes it. See [[report-detail-page.spec]] § Contents load as you scroll.
    hasNextPage: hasNextPage && !isFetchNextPageError,
    fetchNextPage: loadNextPage,
    isFetchingNextPage,
  });

  return (
    <ImageViewerModal
      isOpen={record !== null}
      onClose={onClose}
      imageSrc={record ? (coverImage(record)?.image ?? null) : null}
      projectId={projectId}
      metadataFields={project.metadata_fields}
      projectTags={project.tags}
      projectPlans={project.plans}
      initialRecord={record}
      onNavigatePrev={onPrev ?? undefined}
      onNavigateNext={onNext ?? undefined}
      currentIndex={currentIndex}
      totalCount={totalCount}
      isFetchingNextPage={navIsFetchingNextPage}
      canOnResource={(domain, verb, createdBy) =>
        canOnResource(domain, verb, createdBy)
      }
      canCreate={can("record:Create")}
      canCreateTag={can("tag:Create")}
      canCreateIssue={can("issue:Create")}
      // Reading issues needs no verb, so a View-role member still sees them.
      showRecordIssues
      presentation={presentation}
      onUnsavedChangesChange={onUnsavedChangesChange}
    />
  );
}
